import { AP_INVOICE_PRIORITIES, AP_INVOICE_STATUSES } from '../constants'
import type { ApInvoiceAssignedUser } from '../types'
import { formatInvoicePriority, formatInvoiceStatus } from '../status'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SearchSelect } from '@/components/search-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatMoney } from '@/lib/utils'
import { BulkSelectCheckbox } from './bulk-select-checkbox'
import type { useInvoiceListBulk } from './use-invoice-list-bulk'

export function InvoiceListBulkActions({
  bulk,
  staff,
}: {
  bulk: ReturnType<typeof useInvoiceListBulk>
  staff: ApInvoiceAssignedUser[]
}) {
  const {
    selectedInvoiceUuids,
    setSelectedInvoiceUuids,
    selectedVisibleRows,
    selectedVisibleTotal,
    bulkStatus,
    setBulkStatus,
    bulkPriority,
    setBulkPriority,
    bulkAssignee,
    setBulkAssignee,
    bulkMarkPaid,
    setBulkMarkPaid,
    bulkConfirmOpen,
    setBulkConfirmOpen,
    bulkUpdateInvoices,
    bulkPatch,
    canApplyBulkUpdate,
    bulkChangeLabels,
    resetBulkControls,
    applyBulkUpdates,
  } = bulk

  return (
    <>
      {selectedInvoiceUuids.length > 0 && (
        <div
          aria-label="Bulk invoice actions"
          className="flex flex-col gap-3 rounded-lg bg-muted/50 p-3 md:flex-row md:items-center"
        >
          <div className="min-w-[150px]">
            <p className="text-sm font-medium">{selectedInvoiceUuids.length} selected</p>
            <p className="text-xs tabular-nums text-muted-foreground">
              {formatMoney(selectedVisibleTotal)} on this page
            </p>
          </div>
          <div className="grid flex-1 gap-2 sm:grid-cols-2 lg:grid-cols-[170px_150px_150px_190px_auto_auto]">
            <div className="flex items-center rounded-md border px-3 py-2">
              <BulkSelectCheckbox
                label="Mark paid in full"
                checked={bulkMarkPaid}
                onChange={setBulkMarkPaid}
                showLabel
              />
            </div>
            <SearchSelect
              ariaLabel="Bulk status update"
              className="h-9 w-full"
              value={bulkStatus}
              options={[
                { value: 'keep', label: 'Keep status' },
                ...AP_INVOICE_STATUSES.map((invoiceStatus) => ({
                  value: invoiceStatus,
                  label: formatInvoiceStatus(invoiceStatus),
                })),
              ]}
              onChange={(value) => value && setBulkStatus(value)}
              disabled={bulkMarkPaid}
              placeholder="Status"
              searchPlaceholder="Search statuses"
            />
            <Select value={bulkPriority} onValueChange={setBulkPriority} disabled={bulkMarkPaid}>
              <SelectTrigger aria-label="Bulk priority update">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="keep">Keep priority</SelectItem>
                {AP_INVOICE_PRIORITIES.map((invoicePriority) => (
                  <SelectItem key={invoicePriority} value={invoicePriority}>
                    {formatInvoicePriority(invoicePriority)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <SearchSelect
              ariaLabel="Bulk assignee update"
              className="h-9 w-full"
              value={bulkAssignee}
              options={[
                { value: 'keep', label: 'Keep assignee' },
                { value: 'clear', label: 'Clear assignee' },
                ...staff.map((user) => ({ value: `staff:${user.id}`, label: user.name })),
              ]}
              onChange={(value) =>
                value && setBulkAssignee(value as 'keep' | 'clear' | `staff:${number}`)
              }
              placeholder="Assignee"
              searchPlaceholder="Search staff"
            />
            <LoadingButton
              loading={bulkUpdateInvoices.isPending}
              loadingText="Updating..."
              disabled={!canApplyBulkUpdate}
              onClick={() => setBulkConfirmOpen(true)}
            >
              Apply updates
            </LoadingButton>
            <Button
              type="button"
              variant="outline"
              disabled={!selectedInvoiceUuids.length && Object.keys(bulkPatch).length === 0}
              onClick={() => {
                setSelectedInvoiceUuids([])
                resetBulkControls()
              }}
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm bulk AP update</DialogTitle>
            <DialogDescription>
              Review the intended mutation before it is applied. Ineligible invoices are reported
              individually and remain selected for review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Selected invoices</p>
                <p className="mt-1 text-lg font-semibold tabular-nums">
                  {selectedInvoiceUuids.length}
                </p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Visible selected total</p>
                <p className="mt-1 text-lg font-semibold tabular-nums">
                  {formatMoney(selectedVisibleTotal)}
                </p>
              </div>
            </div>
            {selectedVisibleRows.length !== selectedInvoiceUuids.length && (
              <p className="text-xs text-muted-foreground">
                {selectedInvoiceUuids.length - selectedVisibleRows.length} selected invoice(s) are
                on another page; their amounts are not included in the visible total.
              </p>
            )}
            <div>
              <p className="font-medium">Intended changes</p>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-muted-foreground">
                {bulkChangeLabels.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
            {bulkMarkPaid && (
              <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800">
                This records accounting evidence for each remaining balance. It does not execute,
                schedule, or transmit a payment.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkConfirmOpen(false)}>
              Cancel
            </Button>
            <LoadingButton
              loading={bulkUpdateInvoices.isPending}
              loadingText="Updating..."
              onClick={applyBulkUpdates}
            >
              Confirm {selectedInvoiceUuids.length} update
              {selectedInvoiceUuids.length === 1 ? '' : 's'}
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
